import type { ReactNode } from 'react'
import { type Stripe } from '@stripe/stripe-js'
import { match } from '@/utils/pattern-match-util'
import { RawButton } from '@/components/Button'
import { Icon } from '@/components/Icon'
import { Elements } from '@stripe/react-stripe-js'
import cx from 'classnames'
import type { BillingMethodType } from '@/features/billing/profile/BillingMethod'
import styles from './Payment.module.css'

function PaymentHeader() {
  return (
    <div className={styles.header}>
      <div className={styles.headerTitle}>Complete your payment</div>
      <div className={styles.headerDescription}>
        Review order summary and enter payment details to proceed.
      </div>
    </div>
  )
}

interface PaymentMethodItemCreditProps {
  title: string
}

function PaymentMethodItemCredit({ title }: PaymentMethodItemCreditProps) {
  return (
    <>
      <div className={styles.paymentMethodDescription}>
        <div>{title}</div>
      </div>
    </>
  )
}

interface PaymentMethodItemStripeProps {
  brand: string
  last4: string
  expires: string
}

function PaymentMethodItemStripe({
  brand,
  last4,
  expires,
}: PaymentMethodItemStripeProps) {
  return (
    <>
      <div className={styles.paymentMethodDescription}>
        <span className={styles.descriptionBold}>{brand}</span>{' '}
        <span>ending in</span>{' '}
        <span className={styles.descriptionBold}>{last4}</span>
      </div>
      <div className={styles.paymentMethodExpiration}>
        <span>Expires</span> <span className={styles.expires}>{expires}</span>{' '}
        <span>•</span>{' '}
        <span className={styles.csvCheckPassed}>CSV check passed</span>
      </div>
    </>
  )
}

export interface PaymentMethodItemProps {
  type: BillingMethodType['type']
  title: string
  brand: string
  last4: string
  expires: string
  isSelected: boolean
  onPress: () => void
}

export function PaymentMethodItem({
  type,
  title,
  brand,
  last4,
  expires,
  isSelected,
  onPress,
}: PaymentMethodItemProps) {
  return (
    <RawButton
      aria-pressed={isSelected}
      onPress={onPress}
      className={cx(styles.paymentMethodItem, isSelected && styles.selected)}
    >
      {isSelected && (
        <div className={styles.selectedBadge}>
          <Icon size={12} name="tick" /> Selected
        </div>
      )}
      {match(type, {
        Stripe: () => (
          <PaymentMethodItemStripe
            brand={brand}
            last4={last4}
            expires={expires}
          />
        ),
        Credit: () => <PaymentMethodItemCredit title={title} />,
        StripeCardPaymentMethod: () => (
          <PaymentMethodItemStripe
            brand={brand}
            last4={last4}
            expires={expires}
          />
        ),
        Unknown: () => <div>Unknown Payment Method</div>,
      })}
    </RawButton>
  )
}

export interface PaymentMethodListProps {
  children?: ReactNode
}

export function PaymentMethodList({ children }: PaymentMethodListProps) {
  return <div className={styles.methodsList}>{children}</div>
}

function CardPaymentDivider() {
  return (
    <div className={styles.cardPaymentDivider}>
      <div className={styles.sideDivider}></div>
      <div className={styles.orPayWithCard}>Or pay with card</div>
      <div className={styles.sideDivider}></div>
    </div>
  )
}

export interface PaymentCardFormProps {
  stripe: Stripe | null
  children?: ReactNode
}

export function PaymentCardForm({ stripe, children }: PaymentCardFormProps) {
  return (
    <div className={styles.cardPaymentContainer}>
      <Elements stripe={stripe} options={{ locale: 'en' }}>
        {children}
      </Elements>
    </div>
  )
}

export interface PaymentProps {
  methodList?: ReactNode
  hasPaymentMethods: boolean
  cardForm?: ReactNode
}

export function Payment({
  methodList,
  hasPaymentMethods,
  cardForm,
}: PaymentProps) {
  return (
    <div className={styles.root}>
      <div className={styles.payment}>
        <PaymentHeader />
        {methodList}
        {hasPaymentMethods && <CardPaymentDivider />}
        {cardForm}
      </div>
    </div>
  )
}
